import {
  Badge,
  Button,
  Divider,
  FlexBox,
  IconButton,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { SearchConversationHit, User } from 'lib-shared-types-api';
import moment, { unitOfTime } from 'moment-timezone';
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { appActions } from 'app/app.actions';
import { getConversationTitle } from 'app/utils/conversationTitle';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationLinkModal } from './components/conversationLinkModal/conversationLinkModal';
import { ConversationTimelineDetailsItem } from './components/conversationTimelineDetailsItem';
import {
  ConversationTimelineDetailsSortMenu,
  sortOptions,
} from './components/conversationTimelineDetailsSortMenu';
import styles from './conversationTimelineDetails.module.scss';
import { useConversationsStateEffect } from './utils/useConversationsStateEffect';

type ConversationTimelineDetailsProps = {
  csid: number;
  requesterId: string | undefined;
  handleToggleTimeline: () => void;
};

export const ConversationTimelineDetails = memo(
  function ConversationTimelineDetails({
    csid,
    requesterId,
    handleToggleTimeline,
  }: ConversationTimelineDetailsProps): JSX.Element {
    const dispatch = useAppDispatch();
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedSortOption, setSelectedSortOption] = useState(
      sortOptions[0],
    );

    /**
     * The current value of the requesterId. Used to prevent unnecessary reloads
     * when selecting a different conversation in the timeline.
     *
     * Reason: when a different conversation loads requesterId value becomes
     * undefined until the new conversation is loaded, this current_requester ->
     * undefined -> next_requester change is the reason for reloads.
     */
    const requesterIdArg = useRef<string>();

    const { asyncStatus, items, hasNextPage, changeArg, nextPage } =
      useLazyPaginatedEndpoint({
        endpoint: searchApi.endpoints.readSearchConversation,
        itemsSelector: ({ data }) => data.results.hits,
        infinite: true,
      });

    const { currentlyClosedCsids, currentlyLinkedCsids } =
      useConversationsStateEffect({ requesterId, conversations: items });

    useEffect(() => {
      setSelectedIds([]);
    }, [csid]);

    useEffect(() => {
      if (!requesterId || requesterIdArg.current === requesterId) {
        return;
      }

      requesterIdArg.current = requesterId;

      changeArg({
        filter: {
          facets: {
            requested_by: [requesterId],
          },
        },
        offset: 0,
      });
    }, [changeArg, requesterId]);

    const getTitle = useCallback(
      ({
        initial_direction: direction,
        subject,
        initial_channel,
        requester_email,
        requester_name,
        requester_phone,
      }: SearchConversationHit) =>
        getConversationTitle({
          conversation: {
            direction,
            attributes: subject ? { subject } : undefined,
            initial_channel: initial_channel
              ? (convertChannelCasing(
                  initial_channel,
                  'truncate',
                ) as ConversationsChannels)
              : undefined,
          },
          requester: {
            email: requester_email ?? '',
            name: requester_name ?? '',
            phone_number: requester_phone ?? '',
          } as User,
        }),
      [],
    );

    const onCheck = useCallback(
      (csid: number) => {
        if (selectedIds.includes(csid)) {
          setSelectedIds((ids) => ids.filter((id) => id !== csid));
        } else {
          setSelectedIds((ids) => [...ids, csid]);
        }
      },
      [selectedIds],
    );

    const handleLink = useCallback(async () => {
      ampli.linkConversationsModalOpened();

      const { isLinkInitiated } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:conversationLink',
            arg: { csids: selectedIds, parent_csid: csid },
          },
        }),
      );

      if (isLinkInitiated) {
        setSelectedIds([]);
      }
    }, [csid, dispatch, selectedIds]);

    const handleUnselectAll = useCallback(() => {
      setSelectedIds([]);
    }, []);

    const renderItemsWithSections = useCallback(() => {
      const groups = [
        { label: 'Today', granularity: 'day', used: false },
        { label: 'This week', granularity: 'week', used: false },
        { label: 'This month', granularity: 'month', used: false },
        { label: 'Earlier', used: false },
      ];
      const now = new Date();

      return items.map((conversation) => {
        const group = groups.find(({ granularity }) =>
          granularity
            ? moment(conversation[selectedSortOption.key]).isSame(
                now,
                granularity as unitOfTime.StartOf,
              )
            : true,
        );
        const linkedCsids = currentlyLinkedCsids[conversation.numeric_id];
        const timelineItem = (
          <ConversationTimelineDetailsItem
            key={conversation.numeric_id}
            conversation={conversation}
            title={getTitle(conversation)}
            isCurrentConversation={conversation.numeric_id === csid}
            isChecked={selectedIds.includes(conversation.numeric_id)}
            linkedTo={linkedCsids}
            // Conversation status could be changed after loading the conversation list. Like with bulk actions, etc.
            status={currentlyClosedCsids[conversation.numeric_id]}
            onCheck={onCheck}
          />
        );
        const renderItem =
          group?.used === false ? (
            <Fragment key={group.label}>
              <Divider className={styles.divider}>
                <Text.Paragraph variant="1" subdued>
                  {group.label}
                </Text.Paragraph>
              </Divider>
              {timelineItem}
            </Fragment>
          ) : (
            timelineItem
          );

        if (group?.used === false) {
          group.used = true;
        }

        return renderItem;
      });
    }, [
      csid,
      items,
      currentlyLinkedCsids,
      currentlyClosedCsids,
      selectedIds,
      selectedSortOption.key,
      getTitle,
      onCheck,
    ]);

    return (
      <FlexBox background="neutral.000" flexDirection="column" height="100%">
        <FlexBox
          justifyContent="space-between"
          alignItems="center"
          padding="200 200 200 400"
        >
          {selectedIds.length > 0 ? (
            <FlexBox justifyContent="flex-start" gap="0 100">
              <Button
                onClick={handleLink}
                slotEnd={
                  <Badge variant="secondary" size="small">
                    {/* Parent conversation is always selected, hence N + 1  */}
                    {selectedIds.length + 1}
                  </Badge>
                }
              >
                Link
              </Button>
              <IconButton
                variant="tertiary"
                onClick={handleUnselectAll}
                icon="undo"
              />
            </FlexBox>
          ) : (
            <ConversationTimelineDetailsSortMenu
              selectedSortOption={selectedSortOption}
              changeArg={changeArg}
              onSelectSortOption={setSelectedSortOption}
            />
          )}
          <IconButton
            variant="tertiary"
            onClick={handleToggleTimeline}
            icon={'angle-double-left'}
          />
        </FlexBox>
        <FlexBox className={styles.itemContainer} flexDirection="column">
          {renderItemsWithSections()}
          {asyncStatus === 'pending' && (
            <FlexBox
              as="li"
              padding="300"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner size={20} />
            </FlexBox>
          )}
          {asyncStatus === 'fulfilled' && !items.length && (
            <FlexBox as="li" padding="200" justifyContent="center">
              <Text.Paragraph variant="3" subdued>
                No conversations found.
              </Text.Paragraph>
            </FlexBox>
          )}
          {asyncStatus === 'fulfilled' && hasNextPage && (
            <Waypoint onEnter={nextPage} />
          )}
        </FlexBox>
        <ConversationLinkModal />
      </FlexBox>
    );
  },
);
